// @ts-nocheck
// based on a foundation component but deviated so much at this point its a new component and doesn't need custom tagging

import useParentProductState from "@store/Framework/Store/Foundation/Data/Products/ProductsHooks/useParentProductState";

import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { AttributeTypeModel } from "@insite/client-framework/Types/ApiModels";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreSupercededProductLabel from "@store/Library/Components/Foundation/StoreSupercededProductLabel";
import ProductAttributes, { ProductAttributesStyles } from "@store/Overrides/Components/ProductAttributes";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import ProductTextBadges from "@store/Overrides/Components/ProductTextBadges";
import useHandleMinimumMultupleSalesQuantity from "@store/Framework/Common/Hooks/useHandleMinimumMultupleSalesQuantity";
import { useIsSignedInAndhasToBeSignedIn } from "@store/Framework/Store/Context/ContextHooks";
import MinimumMultipleQuantityWarnings from "@store/Library/Components/Common/MinimumMultipleQuantityWarnings";
import PriceMatrix from "@store/Library/Components/Common/PriceMatrix";
import ProductInventoryDetailMessage, {
    ProductDetailsMessageStyles,
} from "@store/Library/Components/Common/ProductInventoryDetailMessage";
import { productInformationStyles } from "@store/Library/Widgets/ProductList/ProductListProductInformation";
import ProductImageBadges from "@store/Overrides/Components/ProductImageBadges";
import { badgeSizes, getExtendedPrice } from "@store/Overrides/Widgets/MyListsDetails/MyListsDetailsProductListLine";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";
import ProductAddToCartButton from "@store/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@store/Overrides/Components/ProductAddToListLink";
import translate from "@insite/client-framework/Translate";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import Typography from "@insite/mobius/Typography";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { ButtonPresentationProps } from "@insite/mobius/Button";

const PageTitle = getWidgetComponent("ProductDetails/ProductDetailsPageTitle");
const PartNumber = getWidgetComponent("ProductDetails/ProductDetailsPartNumber");
const ManufacturingPartNumber = getWidgetComponent("ProductDetails/ProductDetailsManufacturingPartNumber");
const ProductDetailsPrice = getWidgetComponent("ProductDetails/ProductDetailsPrice");
const ProductDetailsQuantityOrdered = getWidgetComponent("ProductDetails/ProductDetailsQuantityOrdered");
const ProductDetailsVariantOptions = getWidgetComponent("ProductDetails/ProductDetailsVariantOptions");
const ProductDetailsSpecifications = getWidgetComponent("ProductDetails/ProductDetailsSpecifications");
const ProductDetailsAvailability = getWidgetComponent("ProductDetails/ProductDetailsAvailability");

const SizeChart = getWidgetComponent("ProductDetails/SizeChartModal");
type Props = HasProduct & {
    disableOutOfStockVariants: boolean;
    showSizingChart: boolean;
    showSeriesBadge: boolean;
    seriesBadgeWidth: number;
    selectedAttributes: string[];
    fallbackOnNormalAttributeType: boolean;
    widgetId: string;
    disableSwatchGrid: boolean;
};

export interface StoreProductDetailsInformationsStyle {
    componentWrapper?: InjectableCss;
    productName?: InjectableCss;
    productSku?: InjectableCss;
    productDetails?: InjectableCss;
    productDetailsAction?: InjectableCss;
    priceAndAvailabilityWrapper?: InjectableCss;
    productDetailsActionButton?: InjectableCss;
    productDetailsPriceAvailability?: InjectableCss;
    attributes?: ProductAttributesStyles;
    infoBoxMultipleSales?: InjectableCss;
    productDetailsActionQuantityWrapper?: InjectableCss;
    zoomElementStyles?: InjectableCss;
    horizontalRule?: InjectableCss;
    productAddToListLink?: ProductAddToListLinkStyles;
    productInventoryDetailMessage?: ProductDetailsMessageStyles;
    itemTotalTypography?: InjectableCss;
    productAddToCartButton?: ButtonPresentationProps;
    quantityAndButtonWrapper?: InjectableCss;
}

const Spacer = getStyledWrapper("hr");
const style: StoreProductDetailsInformationsStyle = {
    productName: {},
    productAddToListLink: {
        link: {
            typographyProps: {
                css: css`
                    font-size: ${({ theme }) => theme.design.typography.size.smaller};
                    font-weight: ${({ theme }) => theme.design.typography.weight.bold};
                `,
            },
        },
    },
    horizontalRule: {
        css: css`
            width: 100%;
        `,
    },
    productDetailsActionQuantityWrapper: {
        css: css`
            display: flex;
            gap: 1rem;
            align-items: center;
        `,
    },
    componentWrapper: {
        css: css`
            background-color: ${getColor("common.shadesLight")};
            ${breakpointValues([pxToRem(24, 16), null, pxToRem(36)], "padding")}
            ${breakpointValues(["auto", null, null, "unset"], "margin-inline")}
            max-width: 100%;
            flex-direction: column;
            display: flex;
            border-radius: ${pxToRem(16)};
            width: 100%;
            gap: ${({ theme }) => theme.design.layout.spacing.smallest};
            position: relative;
        `,
    },
    productDetails: {
        css: css`
            max-width: 100%;
            column-gap: ${({ theme }) => theme.design.layout.spacing.smallest};
            display: flex;
            flex-direction: column;
            width: 100%;
            row-gap: ${({ theme }) => theme.design.layout.spacing.small};
        `,
    },

    productSku: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            p {
                font-size: ${({ theme }) => theme.design.typography.size.smaller};
                font-weight: ${({ theme }) => theme.design.typography.weight.base};
            }

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            line-height: ${({ theme }) => theme.design.typography.lineHeight.larger};
                        `,
                        null,
                        css`                            
                            line-height: ${({ theme }) => theme.design.typography.lineHeight.small};

                            > * {
                                display: flex;
                                ${contentSpacingStyles(0, "${pxToRem(25)", 0)}}

                                &:nth-child(2) {
                                    position: relative;

                                    &::after {
                                        content: "";
                                        position: absolute;
                                        top: 50%;
                                        left: ${pxToRem(-12)};
                                        width: ${pxToRem(1)};
                                        height: ${({ theme }) => theme.design.typography.size.smaller};
                                        transform: translateY(-50%);
                                    }
                                }
                            }
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    productDetailsAction: {
        css: css`
            display: grid;
            width: 100%;
            row-gap: 1rem;
            margin-bottom: 0;

            > * {
                align-self: center;

                &:first-child {
                    margin-right: 1em;
                    align-self: center;
                }
            }
            input {
                min-width: 4rem;
            }
        `,
    },
    priceAndAvailabilityWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            gap: 1rem;
            margin-bottom: 0;
            > * {
                align-self: center;

                &:nth-child(2) {
                    margin-top: 0;
                }
            }
        `,
    },
    productDetailsActionButton: {
        css: css`
            display: flex;
            align-items: flex-end;

            min-width: 80%;
            gap: ${pxToRem(16)};

            flex-wrap: wrap;
        `,
    },
    attributes: {
        attributeTypeText: {
            css: css`
                font-size: ${({ theme }) => theme.design.typography.size.smaller};
            `,
        },
        attributeValueText: {
            css: css`
                font-size: ${({ theme }) => theme.design.typography.size.smaller};
                font-weight: ${({ theme }) => theme.design.typography.weight.base};
            `,
        },
    },
    productDetailsPriceAvailability: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    infoBoxMultipleSales: {
        css: css`
            max-width: 20em;
            margin: 0 0 0.6em;
        `,
    },
    zoomElementStyles: {
        css: css`
            background-color: white;
            position: absolute;
            overflow: hidden;
            display: none;
            border: 1px solid black;
            box-shadow: ${({ theme }) => theme.shadows.e100Up};
        `,
    },
    productInventoryDetailMessage: {
        wrapper: {
            css: css`
                margin-left: 0;
            `,
        },
    },
    itemTotalTypography: {
        css: css`
            font-weight: 700;
            font-size: ${({ theme }) => theme.design.typography.size.large};
            color: ${getColor("text.milanoRed")};
        `,
    },
    productAddToCartButton: {
        sizeVariant: "medium",
        css: css`
            width: ${pxToRem(246)};
            height: ${pxToRem(42)};
        `,
    },
    quantityAndButtonWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            gap: ${pxToRem(11)};
            align-items: flex-end;
        `,
    },
};

const StoreProductDetailsInformations: React.FC<Props> = ({
    product,
    product: { attributeTypes, detail, variantTraits, specifications, isVariantParent },
    selectedAttributes,
    fallbackOnNormalAttributeType,
    productInfo: { pricing, qtyOrdered },
    productInfo,
    disableSwatchGrid,
}) => {
    const isSignedInAndhasToBeSignedIn = useIsSignedInAndhasToBeSignedIn();
    const { minimumQuantity, multipleQuantity } = useHandleMinimumMultupleSalesQuantity();

    const { variantSelectionCompleted, configurationCompleted } = useSelector((state: ApplicationState) => {
        return {
            variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
            configurationCompleted: state.pages.productDetails.configurationCompleted,
        };
    });
    const selectedAttributesTypes: AttributeTypeModel[] = [];

    if (selectedAttributes && selectedAttributes?.length) {
        selectedAttributes?.forEach(x => {
            attributeTypes?.forEach(y => {
                if (y.name === x) {
                    selectedAttributesTypes.push(y);
                }
            });
        });
    }
    const productContext = { product, productInfo };
    const showTextBadges = true;
    const showImageBadges = true;
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    const replacementProductId = detail?.replacementProductId;
    const parentProductState = useParentProductState();
    const hasAttributes = attributeTypes !== null && attributeTypes?.length !== 0;
    const showSelectedAttributes = selectedAttributes?.length && selectedAttributesTypes.length;
    const showAttribute =
        showSelectedAttributes ||
        (fallbackOnNormalAttributeType && hasAttributes) ||
        (!selectedAttributes?.length && hasAttributes);
    const renderingSpecifications = specifications && specifications.length > 0;
    const showVariants = variantTraits?.some(x => x.traitValues?.length !== 0);
    const renderingParentSpecifications =
        parentProductState?.specifications && parentProductState?.specifications.length > 0;
    const isPriceFactor = Number(pricing?.additionalResults?.priceFactor || 1) > 1;

    const showItemTotal = isSignedInAndhasToBeSignedIn && (qtyOrdered > 1 || isPriceFactor);
    return (
        <StyledWrapper {...style.componentWrapper}>
            <StyledWrapper id="zoomElement" {...style.zoomElementStyles} />
            <StyledWrapper {...style.productDetails}>
                {showTextBadges && textBadges && (
                    <ProductTextBadges
                        extendedStyles={productInformationStyles.productTextBadges}
                        badgeList={textBadges}
                        maxBadges={4}
                    />
                )}
                {showImageBadges && imageBadges && (
                    <ProductImageBadges
                        extendedStyles={productInformationStyles.productImageBadges}
                        badgeSizes={badgeSizes}
                        badgeList={imageBadges}
                        maxBadges={4}
                    />
                )}
                <StyledWrapper {...style.productName}>
                    <PageTitle />
                </StyledWrapper>
                <StyledWrapper {...style.productSku}>
                    <PartNumber />
                    <ManufacturingPartNumber />
                </StyledWrapper>
            </StyledWrapper>
            <Spacer {...style.horizontalRule} />
            {replacementProductId && replacementProductId !== "" ? (
                <StyledWrapper {...style.productDetails}>
                    <StoreSupercededProductLabel />
                </StyledWrapper>
            ) : null}
            <SizeChart />

            {showAttribute || showVariants ? (
                <>
                    <StyledWrapper {...style.productDetails}>
                        <ProductAttributes
                            maximumNumberAttributeTypes={1}
                            selectedAttributesTypes={selectedAttributesTypes}
                            fallbackOnNormalAttributeType={fallbackOnNormalAttributeType}
                            extendedStyles={style.attributes}
                        />

                        <ProductDetailsVariantOptions disableSwatchGrid={disableSwatchGrid} />
                    </StyledWrapper>
                    <Spacer {...style.horizontalRule} />
                </>
            ) : null}
            {/* bunzl custom code end */}
            {!isVariantParent && (
                <StyledWrapper {...style.productDetails}>
                    {isSignedInAndhasToBeSignedIn && (
                        <>
                            <StyledWrapper {...style.priceAndAvailabilityWrapper}>
                                <ProductDetailsPrice />
                                <ProductDetailsAvailability />
                                {showItemTotal && (
                                    <Typography color="text.main" weight={"bold"} size={18}>
                                        {translate("Item Total: ")}
                                        <Typography {...style.itemTotalTypography}>
                                            <LocalizedCurrency amount={getExtendedPrice(pricing, qtyOrdered)} />
                                        </Typography>
                                    </Typography>
                                )}
                            </StyledWrapper>
                            <ProductInventoryDetailMessage extendedStyles={style.productInventoryDetailMessage} />
                            <MinimumMultipleQuantityWarnings
                                multipleQuantity={multipleQuantity}
                                minimumQuantity={minimumQuantity}
                            />
                        </>
                    )}
                    <StyledWrapper {...style.productDetailsAction}>
                        <StyledWrapper {...style.productDetailsActionButton}>
                            <StyledWrapper {...style.quantityAndButtonWrapper}>
                                <ProductDetailsQuantityOrdered />
                                <ProductAddToCartButton
                                    extendedStyles={style.productAddToCartButton}
                                    configurationCompleted
                                    variantSelectionCompleted={variantSelectionCompleted}
                                />
                            </StyledWrapper>
                            <ProductAddToListLink
                                showAsLink
                                hideIcon={false}
                                extendedStyles={style.productAddToListLink}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                    <PriceMatrix />
                </StyledWrapper>
            )}
            <Spacer {...style.horizontalRule} />
            {renderingSpecifications || renderingParentSpecifications ? (
                <StyledWrapper {...style.productDetails}>
                    <ProductDetailsSpecifications showDocuments={false} showAttributes={false} />
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

export default withProduct(StoreProductDetailsInformations);
